import { config, getJson, postJson } from '../ext-core';

import { readLanguageCookie } from './util/cookies';

/**
 * Client fetchers for the multilanguage host routes (all mounted by the
 * codemod at /api/empavision/multilanguage — an @empavision/ext-multilanguage-server
 * sub-router). Everything is fail-soft via ext-core's getJson/postJson: a
 * network/parse error returns null, never throws.
 */

const BASE = '/api/empavision/multilanguage';

/** GET the language the server resolves for this request (+ default source). */
export const fetchSessionLanguage = ({ rootUrl } = {}) =>
  getJson(`${BASE}/session-language`, { rootUrl });

/** POST the chosen language — sets the emp_lang cookie server-side. */
export const postSessionLanguage = (language, { rootUrl } = {}) =>
  postJson(`${BASE}/session-language`, { language }, { rootUrl });

/** POST the hosted config for client-side translation (hydration seam). */
export const translateConfig = (hostedConfig, targetLanguage, { rootUrl } = {}) =>
  postJson(`${BASE}/translate-config`, { hostedConfig, targetLanguage }, { rootUrl });

/** GET the translated hosted-microcopy bundle for a locale. */
export const fetchTranslatedHostedTranslations = (locale, { rootUrl } = {}) =>
  getJson(`${BASE}/translate-hosted-translations/${locale}`, { rootUrl });

/**
 * POST a listing's source title/description → machine translations per target
 * language: { translations: { fr: { title, description }, ... } }.
 *
 * Lived in the host marketplace's own util/api.js until now, which meant every
 * marketplace re-declared the same route. It is the extension's endpoint, so it
 * belongs with the extension's other fetchers.
 */
export const translateListing = ({ listingId, title, description, targetLanguages }, { rootUrl } = {}) =>
  postJson(`${BASE}/translate-listing`, { listingId, title, description, targetLanguages }, { rootUrl });

/** POST CMS page assets ({ [name]: { path, data } }) for client-side translation. */
export const translatePageAssets = (pageAssetsData, targetLanguage, { rootUrl } = {}) =>
  postJson(`${BASE}/translate-page-assets`, { pageAssetsData, targetLanguage }, { rootUrl });

/** POST a batch of source strings ({ id: text }) → { translations: { id: text } }. */
export const translateCatalog = (defaultMessages, targetLanguage, { rootUrl } = {}) =>
  postJson(`${BASE}/translate-catalog`, { defaultMessages, targetLanguage }, { rootUrl });

// Keep each upload well under the HOST's body-size limit. Uploading the whole
// bundled catalog + hosted overlay in one POST (~235KB on a real host) is
// rejected with 413 by the host apiRouter's JSON parser — which fails the whole
// catalog and reverts the client to English (a hydration mismatch vs the
// translated SSR). ~200 keys ≈ ~15-25KB, comfortably under the limit, and aligns
// with the origin's 200-text batch cap.
const CATALOG_UPLOAD_CHUNK = 200;

const sessionStore = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null; // sessionStorage can throw in privacy modes
  }
};

/**
 * Full-catalog translation with a per-session cache (Gap 2).
 *
 * Only the defaults the hosted overlay does NOT already cover are translated
 * (the "residual" — typically a few dozen keys). Those are uploaded in CHUNKS so
 * a large residual never trips the host body-size limit (413), then merged UNDER
 * the already-translated hosted overlay: { ...translatedResidual, ...hosted }.
 * This matches exactly what decorateBundledCatalog returns server-side, so the
 * client hydrates what the SSR rendered (no mismatch).
 *
 * The residual translations are memoised in sessionStorage keyed by (language,
 * residual size); the hosted overlay is re-layered fresh so a mid-session
 * Console edit is never masked by a stale cache.
 *
 * Fail-soft: no sessionStorage / any failure → the plain merge (English defaults
 * + translated hosted), i.e. the pre-Gap-2 behavior.
 */
export const translateCatalogCached = async (defaultMessages, hostedTranslations, language, { rootUrl } = {}) => {
  const hosted = hostedTranslations || {};
  const fallback = { ...defaultMessages, ...hosted };
  try {
    // Residual = defaults the hosted overlay doesn't already cover (+ non-blank).
    const residual = [];
    for (const [key, value] of Object.entries(defaultMessages || {})) {
      if (typeof value === 'string' && value.trim() && !Object.prototype.hasOwnProperty.call(hosted, key)) {
        residual.push([key, value]);
      }
    }
    if (residual.length === 0) return fallback; // hosted covers everything

    const store = sessionStore();
    const cacheKey = `emp_catalog_${language}_${residual.length}`;
    const cached = store ? store.getItem(cacheKey) : null;
    if (cached) {
      return { ...JSON.parse(cached), ...hosted };
    }

    // Chunked upload — each POST stays under the host body-size limit.
    const translatedResidual = {};
    for (let i = 0; i < residual.length; i += CATALOG_UPLOAD_CHUNK) {
      const chunk = Object.fromEntries(residual.slice(i, i + CATALOG_UPLOAD_CHUNK));
      const result = await translateCatalog(chunk, language, { rootUrl });
      // On a failed chunk, fall back to its source strings (English) for those keys.
      Object.assign(translatedResidual, (result && result.translations) || chunk);
    }

    try {
      if (store) store.setItem(cacheKey, JSON.stringify(translatedResidual));
    } catch (_) {
      // quota exceeded — skip caching, still return the translated catalog
    }
    return { ...translatedResidual, ...hosted };
  } catch (_) {
    return fallback;
  }
};

/**
 * The client-side CMS page seam (wired into src/ducks/hostedAssets.duck.js by
 * the codemod — Gap 3). It translates the page bodies the browser re-fetches on
 * CLIENT-side navigation to landing / About / Terms / Privacy.
 *
 * SSR page translation is handled SERVER-side in dataLoader.js (the client
 * fetchers use window.fetch, absent during SSR) — so this returns the source
 * unchanged on the server, and dataLoader does the SSR pass. The language is the
 * `emp_lang` cookie: a visitor only has it after explicitly choosing a language,
 * so the common (default-language) case makes NO origin call.
 *
 * FAIL-SOFT IS ABSOLUTE: SSR / disabled / no chosen language / any failure →
 * the source pageAssets unchanged, so the app renders source text like stock.
 *
 * @param {object} pageAssetsData - { [name]: { path, data } } from the duck
 * @param {{ rootUrl?: string }} [opts]
 * @returns {Promise<object>} translated (or source) pageAssets
 */
export const translatePageAssetsForClient = async (pageAssetsData, { rootUrl } = {}) => {
  try {
    if (typeof window === 'undefined') return pageAssetsData; // SSR handled in dataLoader
    if (!config.isEnabled || !pageAssetsData) return pageAssetsData;
    const language = readLanguageCookie();
    if (!language) return pageAssetsData; // no explicit choice → serve source
    const result = await translatePageAssets(pageAssetsData, language, { rootUrl });
    return (result && result.pageAssetsData) || pageAssetsData;
  } catch (_) {
    return pageAssetsData;
  }
};

/**
 * The pre-hydration seam (wired into src/index.js by the codemod):
 *
 *   const hydration = await prepareHydrationData({ hostedConfig, translations });
 *   <ClientApp hostedTranslations={hydration.translations} hostedConfig={hydration.hostedConfig} />
 *
 * The browser re-fetches hosted assets from the Sharetribe CDN before
 * (re)hydrating, so without this the client would hydrate UNTRANSLATED data
 * over the translated SSR HTML. This asks the host server which language the
 * request resolves to (the SAME resolver SSR used — client and server agree by
 * construction), then translates the config + merges the translated microcopy
 * bundle. Both hit the host's local cache that the SSR pass just populated,
 * so this is host-local and memory-speed.
 *
 * FAIL-SOFT IS ABSOLUTE: any failure returns the inputs unchanged — the app
 * then renders source text exactly as a stock template would.
 *
 * Gap 2: when `defaultMessages` (the bundled en.json) is provided, the FULL
 * catalog is translated (bundled defaults ∪ hosted) so the client hydrates the
 * SAME messages SSR rendered — no hydration mismatch and every <FormattedMessage>
 * is translated, not just the operator-edited hosted keys.
 *
 * @param {{ hostedConfig?: object, translations?: object, defaultMessages?: object, rootUrl?: string }} opts
 * @returns {Promise<{ hostedConfig: object, translations: object, language: string|null }>}
 */
export const prepareHydrationData = async ({ hostedConfig, translations, defaultMessages, rootUrl } = {}) => {
  const source = {
    hostedConfig: hostedConfig || {},
    translations: translations || {},
    language: null,
  };
  try {
    if (!config.isEnabled) return source;

    const session = await fetchSessionLanguage({ rootUrl });
    const language = (session && session.language) || readLanguageCookie();
    const defaultSource = session && session.defaultSource;
    if (!language || language === defaultSource) {
      return { ...source, language: language || null };
    }

    const [configResult, messagesResult] = await Promise.all([
      translateConfig(source.hostedConfig, language, { rootUrl }),
      fetchTranslatedHostedTranslations(language, { rootUrl }),
    ]);

    const translatedHosted = {
      ...source.translations,
      ...((messagesResult && messagesResult.data) || {}),
    };

    // Gap 2: full bundled-catalog translation (defaults ∪ hosted), matching the
    // SSR pass. Skipped (source hosted only) when the codemod didn't hand us the
    // bundled defaults, so this stays backward compatible with an S2 install.
    const finalTranslations = defaultMessages
      ? await translateCatalogCached(defaultMessages, translatedHosted, language, { rootUrl })
      : translatedHosted;

    return {
      hostedConfig: (configResult && configResult.hostedConfig) || source.hostedConfig,
      translations: finalTranslations,
      language,
    };
  } catch (_) {
    return source;
  }
};
